import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../../authProvider.mjs';
import monthlyReportService from '../service/monthlyReportService.mjs';
import { regionLabel } from '../page/PTAERegions.mjs';
import { formatInstant } from '../../utils/dateOnly.mjs';

/**
 * The delegate's side of a delegation: accept or decline being asked to cover
 * monthly report review.
 *
 * Rendered on the home page rather than inside a dashboard because a delegate
 * may be an auditor, an admin or a director, and those three land on three
 * different boards - the home page is the one screen all of them see. It also
 * has to be seen BEFORE they can act on the reports, and until they accept they
 * have no access to the auditor board where the delegation lives.
 *
 * Renders nothing at all when there is no pending invitation, so it costs the
 * home page nothing in the normal case.
 */

/** The end of access, in this reader's timezone. See DelegationModal. */
const fmtDate = (v) => formatInstant(v) || null;

/**
 * The words for each kind of request. An auditor's asks for report REVIEW; a
 * regional or global director's (2026-10-22) asks for their APPROVAL of a stage.
 */
const COPY = {
  auditor: {
    title: 'Cover monthly report review?',
    asks: (who) => `${who || 'An auditor'} has asked you to review monthly reports while they are away.`,
    regionsLabel: 'Regions you would cover',
    noteFrom: (who) => `Note from ${who || 'the auditor'}`,
    grants: 'Accepting gives you access to those regions\' monthly reports in the auditor dashboard, '
      + 'through an Auditor Review card on your own dashboard. Nothing changes if you decline.',
  },
  regional: {
    title: 'Cover regional director approval?',
    asks: (who) => `${who || 'A regional director'} has asked you to approve their region's monthly reports while they are away.`,
    regionsLabel: 'Region',
    noteFrom: (who) => `Note from ${who || 'the regional director'}`,
    grants: 'Accepting lets you approve that region\'s reports at the regional director stage, '
      + 'alongside the director. They reach you by email and from your dashboard. Nothing changes if you decline.',
  },
  final: {
    title: 'Cover final approval?',
    asks: (who) => `${who || 'The global director'} has asked you to give final approval of monthly reports while they are away.`,
    regionsLabel: 'Regions you would cover',
    noteFrom: (who) => `Note from ${who || 'the global director'}`,
    grants: 'Accepting lets you give final approval of those regions\' reports, alongside the '
      + 'global and finance directors. They reach you by email and from your dashboard. Nothing changes if you decline.',
  },
};

const copyFor = (inv) => COPY[inv.kind === 'director' ? inv.stage : 'auditor'] || COPY.auditor;

const DelegationInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    // The service swallows failures and returns [], so a delegation lookup
    // problem cannot stop the rest of the home page rendering. Auditor and
    // director requests live in different stores; tagged so each is answered
    // through its own.
    const [auditor, director] = await Promise.all([
      monthlyReportService.getDelegationInvitations(),
      monthlyReportService.getDirectorDelegationInvitations(),
    ]);
    setInvitations([
      ...auditor.map(i => ({ ...i, kind: 'auditor' })),
      ...director.map(i => ({ ...i, kind: 'director' })),
    ]);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Accepting a delegation grants access on the server immediately, but the
  // module cards are drawn from `capabilities` on /auth/status, which the app
  // only re-reads on load and on the 10-minute token refresh. So the delegate
  // accepted, saw the card not appear, and assumed it had not worked - the
  // access was there the whole time. Re-reading auth status closes that gap.
  const { checkAuthStatus } = useContext(AuthContext);

  const respond = async (invitation, decision, reason = '') => {
    setBusyId(invitation.id);
    setNotice(null);
    try {
      const result = invitation.kind === 'director'
        ? await monthlyReportService.respondToDirectorDelegation(invitation.id, decision, reason)
        : await monthlyReportService.respondToDelegation(invitation.id, decision, reason);
      setNotice({ ok: true, message: result.message });
      setDecliningId(null);
      setDeclineReason('');
      // Drop it from the list rather than reloading: the answer is recorded and
      // the card should go away immediately.
      setInvitations(prev => prev.filter(i => !(i.id === invitation.id && i.kind === invitation.kind)));

      // Silent: the page is already rendered and nothing should flicker - this
      // only refreshes the flags the cards read.
      if (decision === 'accept' && checkAuthStatus) {
        await checkAuthStatus(true);
      }
    } catch (err) {
      setNotice({ ok: false, message: err.message });
    } finally {
      setBusyId(null);
    }
  };

  if (invitations.length === 0 && !notice) return null;

  return (
    <div className="w-full max-w-3xl mx-auto mb-2">
      {notice && (
        <div className={`mb-4 px-4 py-3 rounded-xl border text-sm ${
          notice.ok
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {notice.message}
        </div>
      )}

      {invitations.map(inv => (
        <div
          key={`${inv.kind}-${inv.id}`}
          className="bg-white/95 backdrop-blur-sm border-l-4 border-sky-500 rounded-2xl shadow-xl p-4 sm:p-6 mb-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-gray-800">
                {copyFor(inv).title}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {copyFor(inv).asks(inv.delegatedByName)}
              </p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 whitespace-nowrap">
              Awaiting your response
            </span>
          </div>

          <div className="mt-4 text-sm">
            <p className="text-gray-500 mb-1">{copyFor(inv).regionsLabel}</p>
            <div className="flex flex-wrap gap-2">
              {(inv.regions || []).map(r => (
                <span key={r} className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-800 font-medium">
                  {regionLabel(r)}
                </span>
              ))}
            </div>
          </div>

          {inv.note && (
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                {copyFor(inv).noteFrom(inv.delegatedByName)}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{inv.note}</p>
            </div>
          )}

          {/* The return date is the auditor's own business and is not sent to
              the delegate, so it is only shown if the API includes it. */}
          {/* accessEndsAt, not returnDate: the server only sends returnDate to
              the auditor who created the delegation, so this line could never
              render for the person being invited - which is precisely who needs
              to know when the cover they are accepting runs out. */}
          {fmtDate(inv.accessEndsAt) && (
            <p className="mt-3 text-sm text-gray-500">
              Cover needed until {fmtDate(inv.accessEndsAt)} (your local time)
            </p>
          )}

          <p className="mt-4 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            {copyFor(inv).grants}
          </p>

          {decliningId === inv.id ? (
            <div className="mt-4">
              <label
                htmlFor={`decline-${inv.id}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Why are you declining? <span className="text-stanford-red">*</span>
              </label>
              <textarea
                id={`decline-${inv.id}`}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={2}
                autoFocus
                placeholder="e.g. I am away the same week"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
              />
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-2">
                <button
                  onClick={() => { setDecliningId(null); setDeclineReason(''); }}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => respond(inv, 'decline', declineReason.trim())}
                  disabled={!declineReason.trim() || busyId === inv.id}
                  title={!declineReason.trim() ? 'A reason is required' : ''}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {busyId === inv.id ? 'Saving…' : 'Confirm Decline'}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-col-reverse sm:flex-row flex-wrap justify-end gap-2">
              <button
                onClick={() => { setDecliningId(inv.id); setDeclineReason(''); }}
                disabled={busyId === inv.id}
                className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Decline
              </button>
              <button
                onClick={() => respond(inv, 'accept')}
                disabled={busyId === inv.id}
                className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 font-medium"
              >
                {busyId === inv.id ? 'Saving…' : '✓ Accept'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DelegationInvitations;
